import React from 'react';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { NumberInput } from '../ui/number-input';
import { Label } from '../ui/label';
import { Globe, Send, X, Plus, CheckCircle2, XCircle, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

// Relative "time ago" for the last-delivery badge.
const timeAgo = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

// Small last-delivery status badge next to the Webhook toggle. Clicking it
// expands the last few delivery attempts with their status codes.
const WebhookStatusBadge = ({ api, ruleId }) => {
  const [status, setStatus] = React.useState(null);
  const [loaded, setLoaded] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [logs, setLogs] = React.useState(null);
  const [logsLoading, setLogsLoading] = React.useState(false);
  React.useEffect(() => {
    if (!ruleId) return;
    let active = true;
    (async () => {
      try {
        const res = await api.get(`/event-rules/webhook-status/${ruleId}`);
        if (active) { setStatus(res.data); setLoaded(true); }
      } catch (e) {
        if (active) setLoaded(true);
      }
    })();
    return () => { active = false; };
  }, [api, ruleId]);
  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && logs === null) {
      setLogsLoading(true);
      try {
        const res = await api.get(`/event-rules/webhook-logs/${ruleId}?limit=5`);
        setLogs(res.data?.items || []);
      } catch (e) {
        setLogs([]);
      } finally {
        setLogsLoading(false);
      }
    }
  };
  if (!ruleId || !loaded) return null;
  const hasHistory = status?.found;
  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={hasHistory ? toggle : undefined}
        className={`text-[11px] font-medium flex items-center gap-1 ${hasHistory ? (status.success ? 'text-emerald-600' : 'text-red-600') : 'text-muted-foreground'} ${hasHistory ? 'hover:underline cursor-pointer' : 'cursor-default'}`}
        data-testid="webhook-last-status"
        title={hasHistory ? `Last delivery: ${status.created_at} — click for history` : ''}
      >
        {hasHistory ? (status.success ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />) : null}
        {hasHistory ? (status.success ? `${status.status_code}` : (status.status_code ? `HTTP ${status.status_code}` : 'failed')) : 'Never fired'}
        {hasHistory ? ` · ${timeAgo(status.created_at)}` : ''}
        {hasHistory && (open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
      </button>
      {open && (
        <div className="w-full max-w-[280px] rounded-md border bg-muted/30 p-2 space-y-1" data-testid="webhook-logs-panel">
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Recent deliveries</p>
          {logsLoading ? (
            <p className="text-[11px] text-muted-foreground">Loading…</p>
          ) : (logs && logs.length > 0) ? (
            logs.map((l, i) => (
              <div key={i} className="flex items-center justify-between gap-2 text-[11px]" data-testid={`webhook-log-row-${i}`}>
                <span className={`flex items-center gap-1 ${l.success ? 'text-emerald-600' : 'text-red-600'}`}>
                  {l.success ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                  {l.method || 'POST'} {l.success ? l.status_code : (l.status_code ? `HTTP ${l.status_code}` : 'failed')}
                  {l.attempt > 1 ? ` (try ${l.attempt})` : ''}
                </span>
                <span className="text-muted-foreground whitespace-nowrap">{timeAgo(l.created_at)}</span>
              </div>
            ))
          ) : (
            <p className="text-[11px] text-muted-foreground">No delivery history yet.</p>
          )}
        </div>
      )}
    </div>
  );
};

// Shared Webhook action config — used by Event Rules AND Analytics > Alerts so
// the webhook builder stays identical everywhere. `actions` holds the webhook_*
// fields; `onChange(key, value)` updates them; `testPath` is the backend
// endpoint used by the Test button. `webhookRuleId` (event rule id or alert
// config id) enables the last-delivery status badge.
export const WebhookActionConfig = ({ actions, onChange, api, testPath = '/event-rules/test-webhook', webhookRuleId = null }) => {
  const a = actions || {};
  return (
    <div className="p-3 rounded-lg border space-y-3" data-testid="actions-webhook-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Switch checked={a.webhook_enabled || false} onCheckedChange={(v) => onChange('webhook_enabled', v)} data-testid="actions-webhook-toggle" />
          <div><p className="font-medium text-sm flex items-center gap-1.5"><Globe className="w-4 h-4" /> Webhook</p><p className="text-xs text-muted-foreground">Send HTTP request to external application</p></div>
        </div>
        {a.webhook_enabled && <WebhookStatusBadge api={api} ruleId={webhookRuleId} />}
      </div>
      {a.webhook_enabled && (
        <div className="space-y-3 pl-1">
          <div className="grid grid-cols-4 gap-2">
            <div className="col-span-1 space-y-1">
              <Label className="text-xs">Method</Label>
              <Select value={a.webhook_method || 'POST'} onValueChange={(v) => onChange('webhook_method', v)}>
                <SelectTrigger className="h-8 text-xs" data-testid="actions-webhook-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="POST">POST</SelectItem>
                  <SelectItem value="GET">GET</SelectItem>
                  <SelectItem value="PUT">PUT</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-3 space-y-1">
              <Label className="text-xs">URL</Label>
              <Input value={a.webhook_url || ''} onChange={(e) => onChange('webhook_url', e.target.value)} placeholder="https://example.com/webhook" className="h-8 text-xs" data-testid="actions-webhook-url" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Auth Type</Label>
              <Select value={a.webhook_auth_type || 'none'} onValueChange={(v) => onChange('webhook_auth_type', v)}>
                <SelectTrigger className="h-8 text-xs" data-testid="actions-webhook-auth-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="bearer">Bearer Token</SelectItem>
                  <SelectItem value="basic">Basic Auth</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {a.webhook_auth_type && a.webhook_auth_type !== 'none' && (
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">{a.webhook_auth_type === 'bearer' ? 'Bearer Token' : 'user:password'}</Label>
                <Input type="password" value={a.webhook_auth_token || ''} onChange={(e) => onChange('webhook_auth_token', e.target.value)} placeholder={a.webhook_auth_type === 'bearer' ? 'your-token' : 'user:password'} className="h-8 text-xs font-mono" data-testid="actions-webhook-auth-token" />
              </div>
            )}
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Custom Headers</Label>
            {(a.webhook_headers || []).map((h, idx) => (
              <div key={idx} className="flex gap-1 items-center">
                <Input value={h.key || ''} onChange={(e) => { const hs = [...(a.webhook_headers || [])]; hs[idx] = { ...hs[idx], key: e.target.value }; onChange('webhook_headers', hs); }} placeholder="Header-Name" className="h-7 text-xs flex-1" />
                <Input value={h.value || ''} onChange={(e) => { const hs = [...(a.webhook_headers || [])]; hs[idx] = { ...hs[idx], value: e.target.value }; onChange('webhook_headers', hs); }} placeholder="Header-Value" className="h-7 text-xs flex-1" />
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onChange('webhook_headers', (a.webhook_headers || []).filter((_, i) => i !== idx))}><X className="w-3 h-3" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => onChange('webhook_headers', [...(a.webhook_headers || []), { key: '', value: '' }])}><Plus className="w-3 h-3 mr-1" /> Add Header</Button>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Payload</Label>
            <div className="flex gap-2">
              <button type="button" className={`px-3 py-1 text-xs rounded-md transition-colors ${a.webhook_payload_mode !== 'custom' ? 'bg-background shadow-sm font-medium border' : 'text-muted-foreground hover:text-foreground'}`} onClick={() => onChange('webhook_payload_mode', 'auto')}>Auto (Full Event JSON)</button>
              <button type="button" className={`px-3 py-1 text-xs rounded-md transition-colors ${a.webhook_payload_mode === 'custom' ? 'bg-background shadow-sm font-medium border' : 'text-muted-foreground hover:text-foreground'}`} onClick={() => onChange('webhook_payload_mode', 'custom')}>Custom</button>
            </div>
            {a.webhook_payload_mode === 'custom' && (
              <div className="space-y-1">
                <textarea value={a.webhook_custom_payload || ''} onChange={(e) => onChange('webhook_custom_payload', e.target.value)} placeholder='{"device":"{device_name}","event":"{event_message}"}' className="w-full h-20 text-xs font-mono p-2 border rounded-md bg-background resize-y" data-testid="actions-webhook-custom-payload" />
                <p className="text-[10px] text-muted-foreground">Use placeholders: {'{device_name}'}, {'{rule_name}'}, {'{event_message}'}, {'{severity}'}, {'{event_time}'}, {'{latitude}'}, {'{longitude}'}, {'{map_link}'}, {'{speed_kmh}'}, {'{stage_label}'}</p>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Retry Count (0-5)</Label>
              <NumberInput min="0" max="5" value={a.webhook_retry_count || 0} onValueChange={(n) => onChange('webhook_retry_count', n)} className="h-8 text-xs" data-testid="actions-webhook-retry-count" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Retry Delay (sec)</Label>
              <Input type="number" min="1" value={a.webhook_retry_delay_secs || 5} onChange={(e) => onChange('webhook_retry_delay_secs', Math.max(1, parseInt(e.target.value, 10) || 5))} className="h-8 text-xs" data-testid="actions-webhook-retry-delay" />
            </div>
          </div>
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" data-testid="actions-webhook-test-btn"
            onClick={async () => {
              const url = (a.webhook_url || '').trim();
              if (!url) { toast.error('Please enter a webhook URL first'); return; }
              toast.info('Sending test webhook...');
              try {
                const res = await api.post(testPath, {
                  url,
                  method: a.webhook_method || 'POST',
                  headers: a.webhook_headers || [],
                  auth_type: a.webhook_auth_type || 'none',
                  auth_token: a.webhook_auth_token || '',
                  payload_mode: a.webhook_payload_mode || 'auto',
                  custom_payload: a.webhook_custom_payload || '',
                });
                if (res.data?.success) {
                  toast.success(`Webhook test successful (HTTP ${res.data.status_code})`);
                } else {
                  toast.error(`Webhook test failed: ${res.data?.error || 'Unknown error'}`);
                }
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Webhook test failed');
              }
            }}
          >
            <Send className="w-3 h-3" /> Test Webhook
          </Button>
        </div>
      )}
    </div>
  );
};

export default WebhookActionConfig;
